/**
 * @fileoverview Scout Eval UI Primitives
 * @description Small presentational pieces shared across the Scout evaluation pages.
 * @module features/ScoutEval/components/common
 */

import React from 'react';
import { Box, Breadcrumbs, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronRight, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { BAND_COLORS, MOVEMENT_COLORS, MOVEMENT_LABELS } from '../constants';

export const MODULE_ROOT = '/scout-eval';

/**
 * Render a score chip tinted by its band.
 * @param {Object} props - Score, band, optional label for unscored values, and size.
 * @returns {JSX.Element} Score chip.
 */
export function ScoreChip({ score, band, label, size = 'small' }) {
  const tone = BAND_COLORS[band] || BAND_COLORS['not-run'];
  return (
    <Chip
      size={size}
      className="scout-score"
      label={score === null || score === undefined ? (label || 'Not run') : score.toFixed(2)}
      sx={{ color: tone, backgroundColor: `${tone}16` }}
    />
  );
}

/**
 * Render a signed score movement.
 * @param {Object} props - Score delta.
 * @returns {JSX.Element} Delta indicator.
 */
export function DeltaIndicator({ delta }) {
  if (delta === null || delta === undefined) {
    return <Typography className="scout-delta scout-delta--flat">—</Typography>;
  }
  const improved = delta > 0.005;
  const regressed = delta < -0.005;
  const Icon = improved ? TrendingUp : regressed ? TrendingDown : Minus;
  const tone = improved ? BAND_COLORS.green : regressed ? BAND_COLORS.red : '#5B6573';

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ color: tone }}>
      <Icon size={14} />
      <Typography className="scout-delta">{delta > 0 ? `+${delta.toFixed(2)}` : delta.toFixed(2)}</Typography>
    </Stack>
  );
}

/**
 * Render a movement badge for a compared row.
 * @param {Object} props - Movement key.
 * @returns {JSX.Element} Movement chip.
 */
export function MovementChip({ movement }) {
  const tone = MOVEMENT_COLORS[movement] || MOVEMENT_COLORS.unchanged;
  return (
    <Chip
      size="small"
      className="scout-movement"
      label={MOVEMENT_LABELS[movement]}
      sx={{ color: tone, backgroundColor: `${tone}14`, borderColor: `${tone}33` }}
    />
  );
}

/**
 * Render one summary metric tile.
 * @param {Object} props - Metric label, value, caption, icon, and accent color.
 * @returns {JSX.Element} Summary metric.
 */
export function SummaryMetric({ label, value, caption, icon: Icon, color, children }) {
  return (
    <Paper className="scout-metric" elevation={0}>
      <Box className="scout-metric-icon" sx={{ color, backgroundColor: `${color}14` }}>
        <Icon size={20} />
      </Box>
      <Box minWidth={0}>
        <Typography className="scout-metric-label">{label}</Typography>
        <Typography className="scout-metric-value">{value}</Typography>
        {children || <Typography className="scout-metric-caption">{caption}</Typography>}
      </Box>
    </Paper>
  );
}

/**
 * Render the navigation trail for the current depth.
 * @param {Object} props - Ordered trail entries.
 * @returns {JSX.Element} Breadcrumb trail.
 */
export function ScoutBreadcrumbs({ trail }) {
  return (
    <Breadcrumbs className="scout-breadcrumbs" separator={<ChevronRight size={13} />} aria-label="Scout evaluation navigation">
      <Link component={RouterLink} to={MODULE_ROOT} underline="hover">Scout Evals</Link>
      {trail.map((entry) => (
        entry.to
          ? <Link key={entry.label} component={RouterLink} to={entry.to} underline="hover">{entry.label}</Link>
          : <Typography key={entry.label} className="scout-breadcrumb-current">{entry.label}</Typography>
      ))}
    </Breadcrumbs>
  );
}

/**
 * Render a centered empty state.
 * @param {Object} props - Icon, title, message, actions, and hint.
 * @returns {JSX.Element} Empty state.
 */
export function EmptyState({ icon: Icon, title, message, children, hint }) {
  return (
    <Paper className="scout-empty-state" elevation={0}>
      <Box className="scout-empty-icon"><Icon size={28} /></Box>
      <Typography variant="h6" className="scout-empty-title">{title}</Typography>
      <Typography className="scout-empty-text">{message}</Typography>
      {children && (
        <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>{children}</Stack>
      )}
      {hint && <Typography className="scout-empty-hint">{hint}</Typography>}
    </Paper>
  );
}
